import { Form, Input, Modal, Select, Tag } from "antd";
import { handleDate, idToObjectMap } from "common/utils";
import { TableFormList } from "components/formList";
import currency from "currency.js";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import {
  useAddSettlementAndPaymentRuleMutation,
  useAddSettlementAndPaymentRuleSetMutation,
  useDeleteSettlementAndPaymentRuleMutation,
  useFetchPaymentRulesQuery,
  useFetchSettlementAndPaymentRulesQuery,
  useFetchSettlementRulesQuery,
  useUpdateSettlementAndPaymentRuleMutation,
  useUpdateSettlementAndPaymentRuleSetMutation,
} from "features/settlementAndPaymentRules/settlementAndPaymentRulesAPI";
import { useFetchWorkflowStatesQuery } from "features/workflows/workflowStatesAPI";
import { useMemo } from "react";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const StyledDiv = styled.div`
  margin-bottom: 12px;
`;

const filterOption = (inputValue, option) => {
  const fullOptionText = option.label;
  return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
};

export default function AddSettlementAndPaymentRulesetModal({
  title,
  open,
  onOk,
  onCancel,
  selectedRuleSet,
  setSelectedRuleSet,
}) {
  const [form] = Form.useForm();
  const { data: paymentRules, isLoading: isPaymentRulesLoading } = useFetchPaymentRulesQuery();
  const { data: settlementRules, isLoading: isSettlementRulesLoading } =
    useFetchSettlementRulesQuery();
  const { data: productOfferings } = useFetchProductOfferingsQuery();
  const { data: defaultWorkflowStates } = useFetchWorkflowStatesQuery();
  const [addSettlementAndPaymentRule] = useAddSettlementAndPaymentRuleMutation();
  const [updateSettlementAndPaymentRule] = useUpdateSettlementAndPaymentRuleMutation();
  const [addSettlementAndPaymentRuleSet] = useAddSettlementAndPaymentRuleSetMutation();
  const [updateSettlementAndPaymentRuleSet] = useUpdateSettlementAndPaymentRuleSetMutation();
  const [deleteSettlementAndPaymentRule] = useDeleteSettlementAndPaymentRuleMutation();

  const initialValues = {
    ...selectedRuleSet,
    defaultSettlementRuleId: selectedRuleSet?.defaultSettlementRule?.id,
    defaultPaymentRuleId: selectedRuleSet?.defaultPaymentRule?.id,
  };

  const isNew = !selectedRuleSet;

  const { data: settlementAndPaymentRules, isLoading } = useFetchSettlementAndPaymentRulesQuery(
    {
      ruleSetId: selectedRuleSet?.id,
    },
    { skip: isNew },
  );

  const productOfferingsById = useMemo(() => {
    return idToObjectMap(productOfferings);
  }, [productOfferings]);

  const save = async () => {
    const fields = await form.validateFields();
    const action = isNew
      ? addSettlementAndPaymentRuleSet(fields)
      : updateSettlementAndPaymentRuleSet({ ...fields, id: selectedRuleSet.id });
    const result = await action;

    if (isNew && "data" in result) {
      setSelectedRuleSet(result.data);
    }
    return result;
  };

  const onSubmit = async () => {
    const result = await save();
    if ("data" in result) {
      onOk();
    }
  };

  // We run this hook when Add Payment And Settlement Rule is pressed
  const preAddSettlementAndPaymentRowHook = async () => {
    if (isNew) {
      const result = await save();
      if ("error" in result) {
        return false;
      }
    }
  };

  const columns = [
    {
      dataIndex: "paymentRuleId",
      key: "paymentRuleId",
      render: (index, rule) =>
        rule.paymentRuleId
          ? paymentRules?.find((paymentRule) => paymentRule.id === rule.paymentRuleId)?.name ??
            `Payment Rule #${rule.paymentRuleId}`
          : "-",
      editable: true,
      inputType: "select",
      title: "Payment Rule",
      options: [
        {
          label: "-------",
          value: null,
        },
        ...(paymentRules?.map((paymentRule) => ({
          label: paymentRule.name,
          value: paymentRule.id,
        })) ?? []),
      ],
      loading: isPaymentRulesLoading,
    },
    {
      dataIndex: "settlementRuleId",
      key: "settlementRuleId",
      render: (index, rule) =>
        rule.settlementRuleId
          ? settlementRules?.find((settlementRule) => settlementRule.id === rule.settlementRuleId)
              ?.name ?? `Settlement Plan #${rule.settlementRuleId}`
          : "-",
      editable: true,
      inputType: "select",
      title: "Settlement Plan",
      options: [
        {
          label: "-------",
          value: null,
        },
        ...(settlementRules?.map((settlementRule) => ({
          label: settlementRule.name,
          value: settlementRule.id,
        })) ?? []),
      ],
      loading: isSettlementRulesLoading,
    },
    {
      title: "Account Status",
      dataIndex: "workflowStateIds",
      key: "workflowStateIds",
      editable: true,
      inputType: "select",
      inputProps: {
        mode: "multiple",
        filterOption,
      },
      width: 600,
      options: defaultWorkflowStates?.map((state) => ({
        value: state.id,
        label: [state.code, state.name].join(" - "),
      })),
      render: (index, rule) => {
        const workflowStates =
          defaultWorkflowStates?.filter((state) => rule.workflowStateIds?.includes(state.id)) ?? [];

        if (workflowStates.length === 0) return "-";
        return workflowStates.map((state) => (
          <Tag key={`rule-${rule?.id}-workflow-state-${state.id}`}>
            {state.code} - {state.name}
          </Tag>
        ));
      },
    },
    {
      title: "Legal Status",
      dataIndex: "hasLawsuit",
      key: "hasLawsuit",
      editable: true,
      inputType: "select",
      options: [
        { label: "-------", value: null, key: "null" },
        { label: "Contains Legal", value: true, key: "yes" },
        { label: "No Legal", value: false, key: "no" },
      ],
      render: (index, rule) =>
        ({
          true: <Tag color="red">Contains Legal</Tag>,
          false: <Tag color="green">No Legal</Tag>,
          null: "-",
        }[rule.hasLawsuit]),
    },
    {
      title: "Product",
      dataIndex: "productOfferingId",
      key: "productOfferingId",
      editable: true,
      inputType: "select",
      options: productOfferings?.map((productOffering) => ({
        label: productOffering.name,
        value: productOffering.id,
      })),
      render: (index, rule) => productOfferingsById[rule.productOfferingId]?.name ?? "-",
    },
    {
      dataIndex: "minAccountBalance",
      key: "minAccountBalance",
      editable: true,
      inputType: "number",
      title: "Account Original Balance (From)",
      render: (amount) => (amount ? `$${currency(amount, { precision: 4 }).value}` : "-"),
    },
    {
      dataIndex: "maxAccountBalance",
      key: "maxAccountBalance",
      editable: true,
      inputType: "number",
      title: "Account Original Balance (To)",
      render: (amount) => (amount ? `$${currency(amount, { precision: 4 }).value}` : "-"),
    },
    {
      dataIndex: "minAccountServiceAgeDays",
      key: "minAccountServiceAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days Since Service Date (From)",
    },
    {
      dataIndex: "maxAccountServiceAgeDays",
      key: "maxAccountServiceAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Service Date (To)",
    },
    {
      dataIndex: "minAccountAgeDays",
      key: "minAccountAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days in Aktos (From)",
    },
    {
      dataIndex: "maxAccountAgeDays",
      key: "maxAccountAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days in Aktos (To)",
    },
    {
      dataIndex: "minAccountUploadAgeDays",
      key: "minAccountUploadAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days Since Upload (From)",
    },
    {
      dataIndex: "maxAccountUploadAgeDays",
      key: "maxAccountUploadAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Upload (To)",
    },
    {
      dataIndex: "minFirstDelinquencyAgeDays",
      key: "minFirstDelinquencyAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days Since First Delinquency (From)",
    },
    {
      dataIndex: "maxFirstDelinquencyAgeDays",
      key: "maxFirstDelinquencyAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since First Delinquency (To)",
    },
    {
      dataIndex: "minDateOfFirstDelinquency",
      key: "minDateOfFirstDelinquency",
      editable: true,
      required: false,
      inputType: "date",
      title: "Date of First Delinquency (From)",
    },
    {
      dataIndex: "maxDateOfFirstDelinquency",
      key: "maxDateOfFirstDelinquency",
      required: false,
      editable: true,
      inputType: "date",
      title: "Date of First Delinquency (To)",
    },
    {
      dataIndex: "minTurnoverDate",
      key: "minTurnoverDate",
      editable: true,
      required: false,
      inputType: "date",
      title: "Turnover Date (From)",
    },
    {
      dataIndex: "maxTurnoverDate",
      key: "maxTurnoverDate",
      required: false,
      editable: true,
      inputType: "date",
      title: "Turnover Date (To)",
    },
    {
      dataIndex: "minTurnoverAgeDays",
      key: "minTurnoverAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Turnover (From)",
    },
    {
      dataIndex: "maxTurnoverAgeDays",
      key: "maxTurnoverAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Turnover (To)",
    },
  ];

  return (
    <Modal
      width={1200}
      maskClosable={false}
      title={title}
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        form={form}
        layout="vertical"
        validateMessages={{ required: "This is a required field" }}
        initialValues={initialValues}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "This field is required." }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="defaultPaymentRuleId"
          label="Default Payment Rule"
          tooltip="Default payment rule when no payment rules match"
        >
          <Select
            placeholder="Select Payment Rule"
            options={paymentRules?.map((paymentRule) => ({
              label: paymentRule.name,
              value: paymentRule.id,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="defaultSettlementRuleId"
          label="Default Settlement Rule"
          tooltip="Default settlement rule when no settlement rules match"
        >
          <Select
            placeholder="Select Settlement Plan"
            options={settlementRules?.map((settlementRule) => ({
              label: settlementRule.name,
              value: settlementRule.id,
            }))}
          />
        </Form.Item>
      </StyledForm>
      <h5>Payment And Settlement Rule</h5>
      <StyledDiv>
        Define payment and settlement rules that define which rules to use for each account criteria
        matched.
      </StyledDiv>
      <TableFormList
        addText="Add Rule"
        columns={columns}
        data={isNew ? [] : settlementAndPaymentRules}
        onDelete={(settlementAndPaymentRule) =>
          deleteSettlementAndPaymentRule({
            ruleSetId: selectedRuleSet.id,
            ...settlementAndPaymentRule,
          })
        }
        onSave={async (settlementAndPaymentRule) => {
          const action = settlementAndPaymentRule?.id
            ? updateSettlementAndPaymentRule
            : addSettlementAndPaymentRule;
          const {
            minTurnoverDate,
            maxTurnoverDate,
            minDateOfFirstDelinquency,
            maxDateOfFirstDelinquency,
            ...ruleFields
          } = settlementAndPaymentRule;
          const payload = {
            ruleSetId: selectedRuleSet.id,
            ...ruleFields,
            minTurnoverDate: handleDate(minTurnoverDate, true),
            maxTurnoverDate: handleDate(maxTurnoverDate, true),
            minDateOfFirstDelinquency: handleDate(minDateOfFirstDelinquency, true),
            maxDateOfFirstDelinquency: handleDate(maxDateOfFirstDelinquency, true),
          };
          return action(payload);
        }}
        loading={isLoading}
        preAddRowHook={preAddSettlementAndPaymentRowHook}
      />
    </Modal>
  );
}
